import { type ComponentProps, useEffect, useRef, useState } from "react"
import { UnsavedChanges } from "~/components/UnsavedChanges"
import { formSignature } from "~/lib/form"

export function SettingsForm({
	children,
	onSave,
	onReset,
	isSubmitting = false,
	readOnly = false,
	className = "flex flex-col gap-6",
	...props
}: Omit<ComponentProps<"form">, "onSubmit" | "onReset"> & {
	onSave: (form: HTMLFormElement) => Promise<void>
	onReset?: (saved: FormData) => void
	isSubmitting?: boolean
	readOnly?: boolean
}) {
	const formRef = useRef<HTMLFormElement>(null)
	const saved = useRef<FormData | null>(null)
	const [baseline, setBaseline] = useState("")
	const [signature, setSignature] = useState("")
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		const form = formRef.current
		if (!form || saving || isSubmitting) return
		const data = new FormData(form)
		const next = formSignature(data)
		if (!saved.current) {
			saved.current = data
			setBaseline(next)
		}
		setSignature(next)
	})

	return (
		<form
			{...props}
			ref={formRef}
			className={className}
			onChangeCapture={() => {
				requestAnimationFrame(() => {
					if (formRef.current?.isConnected && !saving && !isSubmitting)
						setSignature(formSignature(new FormData(formRef.current)))
				})
			}}
			onSubmit={(event) => {
				event.preventDefault()
				if (
					!readOnly &&
					formSignature(new FormData(event.currentTarget)) !== baseline &&
					!saving &&
					!isSubmitting
				) {
					const form = event.currentTarget
					const data = new FormData(form)
					setSaving(true)
					void onSave(form)
						.then(() => {
							for (const control of form.elements) {
								if (control instanceof HTMLInputElement) {
									if (control.type === "file") {
										control.value = ""
										data.delete(control.name)
									} else if (
										control.type === "checkbox" ||
										control.type === "radio"
									) {
										control.defaultChecked = data
											.getAll(control.name)
											.includes(control.value)
									} else {
										control.defaultValue = String(data.get(control.name) ?? "")
									}
								} else if (control instanceof HTMLTextAreaElement)
									control.defaultValue = String(data.get(control.name) ?? "")
								else if (control instanceof HTMLSelectElement)
									for (const option of control.options)
										option.defaultSelected = data
											.getAll(control.name)
											.includes(option.value)
							}
							saved.current = data
							setBaseline(formSignature(data))
						})
						.catch(() => {})
						.finally(() => setSaving(false))
				}
			}}
		>
			<fieldset
				disabled={readOnly || saving || isSubmitting}
				className="m-0 flex min-w-0 flex-col gap-6 border-0 p-0"
			>
				{children}
			</fieldset>
			<UnsavedChanges
				isVisible={!readOnly && signature !== baseline}
				pending={saving || isSubmitting}
				canSave={!readOnly && !saving && !isSubmitting}
				onSave={async () => {
					formRef.current?.requestSubmit()
				}}
				onReset={async () => {
					if (saving || isSubmitting) return
					formRef.current?.reset()
					if (saved.current) onReset?.(saved.current)
					setSignature(baseline)
				}}
			/>
		</form>
	)
}
